import { ChatboxPosition, Crisp } from 'crisp-sdk-web';
import type { User } from 'api/types/user';
import { SUBSCRIPTION_ACTIVE_STATUSES } from 'api/types/user';

const CRISP_WEBSITE_ID = import.meta.env.VITE_CRISP_WEBSITE_ID?.trim() ?? '';

let configured = false;
let loaded = false;
let preferLauncherHidden = false;
let currentIdentityKey: string | null = null;

const buildNickname = (user: User): string => {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return [fullName, user.discordUsername, user.email].find((value) => value?.length) ?? user.id;
};

const buildIdentityKey = (user: User, crispToken: string): string =>
  [user.id, user.email ?? '', buildNickname(user), crispToken].join(':');

const ensureCrispLoaded = () => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  ensureCrispConfigured();
  if (!loaded) {
    Crisp.load();
    Crisp.chat.onChatClosed(() => {
      if (preferLauncherHidden) {
        Crisp.chat.hide();
      }
    });
    loaded = true;
  }
};

export const ensureCrispConfigured = () => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  if (configured) {
    return;
  }

  Crisp.configure(CRISP_WEBSITE_ID, { autoload: false });
  Crisp.setPosition(ChatboxPosition.Left);
  configured = true;
};

export const openCrispChat = () => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  try {
    ensureCrispLoaded();
    Crisp.chat.show();
    Crisp.chat.open();
  } catch (error) {
    console.warn('Failed to open Crisp chat:', error);
  }
};

export const openCrispChatWithMessage = (text: string) => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  try {
    ensureCrispLoaded();
    Crisp.chat.show();
    Crisp.chat.open();
    // Prefill the composer with a starting message; identity/continuity is
    // preserved (we intentionally do not reset the session, which would drop it).
    Crisp.message.setMessageText(text);
  } catch (error) {
    console.warn('Failed to open Crisp chat with message:', error);
  }
};

export const showAnonymousCrispLauncher = () => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  try {
    ensureCrispLoaded();
    preferLauncherHidden = false;
    Crisp.chat.show();
  } catch (error) {
    console.warn('Failed to show anonymous Crisp launcher:', error);
  }
};

export const setCrispLauncherVisible = (visible: boolean) => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  try {
    preferLauncherHidden = !visible;
    if (!loaded) {
      return;
    }
    if (visible) {
      Crisp.chat.show();
    } else if (!Crisp.chat.isChatOpened()) {
      Crisp.chat.hide();
    }
  } catch (error) {
    console.warn('Failed to update Crisp launcher visibility:', error);
  }
};

export const syncCrispUser = (user: User, crispToken: string) => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  try {
    ensureCrispConfigured();

    const nextIdentityKey = buildIdentityKey(user, crispToken);
    const needsRebind = loaded && currentIdentityKey !== nextIdentityKey;

    if (currentIdentityKey && currentIdentityKey !== nextIdentityKey) {
      resetCrispSession();
    }

    Crisp.setTokenId(crispToken);
    if (needsRebind) {
      // Crisp only applies a token changed after script injection on reset.
      Crisp.session.reset();
    }

    if (user.email) {
      Crisp.user.setEmail(user.email);
    }
    Crisp.user.setNickname(buildNickname(user));

    const avatarUrl = user.googleAvatar ?? user.discordAvatar;
    if (avatarUrl) {
      Crisp.user.setAvatar(avatarUrl);
    }

    const hasActiveSubscription = Boolean(
      user.stripeSubscriptionStatus &&
        SUBSCRIPTION_ACTIVE_STATUSES.includes(user.stripeSubscriptionStatus)
    );

    Crisp.session.setData({
      user_id: user.id,
      auth0_id: user.auth0Id,
      app: "The DM's Ark",
      platform: 'web',
      subscription_status: user.stripeSubscriptionStatus ?? 'none',
      has_active_subscription: String(hasActiveSubscription),
      discord_username: user.discordUsername ?? '',
      primary_provider: user.primaryProvider ?? '',
      user_created_at: user.createdAt ? String(user.createdAt) : ''
    });

    Crisp.session.setSegments(
      ['web-app', hasActiveSubscription ? 'subscriber' : 'non-subscriber'],
      true
    );

    if (!loaded) {
      ensureCrispLoaded();
    }

    currentIdentityKey = nextIdentityKey;
  } catch (error) {
    console.error('Failed to sync Crisp user:', error);
  }
};

export const resetCrispSession = () => {
  if (!CRISP_WEBSITE_ID) {
    return;
  }

  try {
    ensureCrispConfigured();
    Crisp.setTokenId();
    Crisp.session.reset();
    currentIdentityKey = null;
  } catch (error) {
    console.error('Failed to reset Crisp session:', error);
  }
};
